import { createApiInstance } from './axios-config';
import API_CONFIG from '../../config/apiConfig';

const api = createApiInstance(API_CONFIG.AGENT_SERVICE_URL);

export interface Agent {
  id: string;
  name: string;
  type: string;
  custom_name?: string;
  system_prompt: string;
  llm_provider: string;
  llm_model: string;
  temperature: number;
  max_tokens: number;
  avatar_url?: string;
  color?: string;
  avatar_fallback_color?: string;
  is_active: boolean;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export class AgentService {
  async getAgents(includeInactive = false) {
    console.log('[AgentService] Getting agents...', { includeInactive });
    try {
      const response = await api.get('/api/v1/agents', {
        params: { includeInactive },
      });
      console.log('[AgentService] Got agents response:', response.data);
      
      // If no agents, try to initialize them
      if (response.data.agents.length === 0) {
        console.log('[AgentService] No agents found, attempting to initialize...');
        await this.initializeAgents();
        // Retry getting agents
        const retryResponse = await api.get('/api/v1/agents', {
          params: { includeInactive },
        });
        console.log('[AgentService] After initialization, got agents:', retryResponse.data);
        return retryResponse.data.agents;
      }
      
      return response.data.agents;
    } catch (error: any) {
      console.error('[AgentService] Error getting agents:', error);
      console.error('[AgentService] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.config?.headers
      });
      throw error;
    }
  }
  
  async initializeAgents() {
    console.log('[AgentService] Initializing agents...');
    try {
      const response = await api.post('/api/v1/agents/initialize', {});
      console.log('[AgentService] Initialize response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[AgentService] Failed to initialize agents:', error);
      if (error.response?.data?.error === 'Agents already initialized for user') {
        console.log('[AgentService] Agents already initialized');
        return { agents: [], message: 'Already initialized' };
      }
      throw error;
    }
  }

  async getAgent(agentId: string) {
    const response = await api.get(`/api/v1/agents/${agentId}`);
    return response.data;
  }

  async updateAgent(agentId: string, data: { custom_name?: string; custom_prompt?: string }) {
    const response = await api.patch(`/api/v1/agents/${agentId}`, data);
    return response.data;
  }

  async toggleAgent(agentId: string) {
    const response = await api.put(`/api/v1/agents/${agentId}/toggle`, {});
    return response.data;
  }

  async createAgent(data: {
    name: string;
    type: string;
    systemPrompt: string;
    llmProvider: string;
    llmModel: string;
    settings?: Record<string, any>;
  }) {
    const response = await api.post('/api/v1/agents', data);
    return response.data;
  }

  async uploadDocument(
    file: File,
    conversationId?: string,
    onProgress?: (progress: number) => void,
    options?: { scope?: 'personal' | 'organization' }
  ) {
    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) {
      formData.append('conversationId', conversationId);
    }
    if (options?.scope) {
      formData.append('scope', options.scope);
    }

    const response = await api.post('/api/v1/documents/upload', formData, {
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    return response.data;
  }

  async uploadDocumentBatch(
    files: File[],
    options: {
      scope: 'personal' | 'organization';
      conversationId?: string;
    },
    onProgress?: (progress: number) => void
  ): Promise<{
    batchId: string;
    totalFiles: number;
    status: string;
    message: string;
    files: Array<{
      documentId: number;
      filename: string;
      mimetype: string;
      sizeBytes: number;
      status: string;
    }>;
  }> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('scope', options.scope);
    if (options.conversationId) {
      formData.append('conversationId', options.conversationId);
    }

    const response = await api.post('/api/v1/documents/batch-upload', formData, {
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
      timeout: 5 * 60 * 1000, // 5 minute upload timeout for large batches
    });

    return response.data;
  }

  async getBatchUploadStatus(batchId: string): Promise<{
    batchId: string;
    status: string;
    totalFiles: number;
    completedFiles: number;
    failedFiles: number;
    currentFileName: string | null;
    currentFileIndex: number;
    progress: number;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    files: Array<{
      documentId: number;
      filename: string;
      mimetype: string;
      sizeBytes: number;
      status: string;
      progress: number;
      knowledgeGraphNodeId: string | null;
    }>;
  }> {
    const response = await api.get(`/api/v1/documents/batch/${batchId}/status`);
    return response.data;
  }
}